/**
 * « Ajouter à mon calendrier » pour une date fixée (spec S3, 01/10/2026).
 *
 * Cinq cibles, sans OAuth : Apple (fichier .ics), Google, Office 365, Outlook
 * (compte perso) et Yahoo (liens de composition). Tout est calculé côté
 * navigateur — le même module sert à l'e-mail « La date est fixée ».
 *
 * Deux choix de la spec, à ne pas défaire :
 * - un créneau sans heure devient un événement « journée entière » ;
 * - l'heure est celle de l'ORGANISATEUR (`timezone` enregistré avec la date).
 *   On la convertit en UTC ici : un votant à l'étranger voit donc la bonne
 *   heure locale, quel que soit le fuseau de son navigateur.
 *
 * Le .ics porte un UID stable (sondage) et un SEQUENCE : renvoyer le fichier
 * après un changement de date avec SEQUENCE+1 met l'événement à jour dans la
 * plupart des agendas, au lieu d'en créer un second.
 */

export interface FinalSlot {
  /** Jour retenu, `YYYY-MM-DD`. */
  date: string;
  /** Dernier jour inclus d'un groupe de jours (week-end), journée entière. */
  endDate?: string | null;
  /** Heure de début `HH:MM` ; absente → journée entière. */
  start?: string | null;
  /** Heure de fin `HH:MM` ; absente → début + durée par défaut. */
  end?: string | null;
  /** Fuseau IANA de l'organisateur au moment où il a fixé la date. */
  timezone?: string | null;
}

export interface FinalDateEvent {
  /** Identifiant stable de l'événement (l'id du sondage suffit). */
  uid: string;
  /** Numéro de révision ; +1 à chaque changement de date. */
  sequence?: number;
  title: string;
  description?: string;
  /** Lien vers la page du sondage. */
  url?: string;
  slot: FinalSlot;
}

export type CalendarTarget = "apple" | "google" | "office365" | "outlook" | "yahoo";

export const CALENDAR_TARGETS: CalendarTarget[] = [
  "apple",
  "google",
  "office365",
  "outlook",
  "yahoo",
];

/** Durée appliquée quand le créneau n'a pas d'heure de fin (spec : 1 h). */
export const DEFAULT_DURATION_MINUTES = 60;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isAllDay(slot: FinalSlot): boolean {
  return !slot.start || !TIME_RE.test(slot.start);
}

export function isValidFinalSlot(value: unknown): value is FinalSlot {
  if (!value || typeof value !== "object") return false;
  const s = value as FinalSlot;
  if (typeof s.date !== "string" || !DATE_RE.test(s.date)) return false;
  if (s.start != null && !TIME_RE.test(s.start)) return false;
  if (s.end != null && !TIME_RE.test(s.end)) return false;
  if (s.endDate != null && (!DATE_RE.test(s.endDate) || s.endDate < s.date)) return false;
  return true;
}

/** Fuseau du navigateur, avec repli sûr. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Décalage (minutes) du fuseau `timeZone` à l'instant `utcMs`.
 * Positif à l'est de Greenwich (Paris l'été : +120).
 */
function tzOffsetMinutes(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return Math.round((asUtc - utcMs) / 60000);
}

/**
 * Convertit une heure murale (`date` + `HH:MM`) d'un fuseau IANA en instant UTC.
 * Deux passes pour tomber juste autour des changements d'heure.
 */
export function wallTimeToUtc(date: string, time: string, timeZone: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const naive = Date.UTC(y, m - 1, d, hh, mm);
  let zone = timeZone;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
  } catch {
    zone = "UTC";
  }
  let guess = naive - tzOffsetMinutes(naive, zone) * 60000;
  guess = naive - tzOffsetMinutes(guess, zone) * 60000;
  return new Date(guess);
}

function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

/** Bornes de l'événement : instants UTC (avec heure) ou jours (journée entière). */
export type EventBounds =
  | { allDay: true; kind: "day"; startDay: string; endDayExclusive: string }
  | { allDay: false; kind: "time"; start: Date; end: Date };

export function resolveEventBounds(slot: FinalSlot): EventBounds {
  if (isAllDay(slot)) {
    const last =
      slot.endDate && DATE_RE.test(slot.endDate) && slot.endDate > slot.date
        ? slot.endDate
        : slot.date;
    return { allDay: true, kind: "day", startDay: slot.date, endDayExclusive: addDays(last, 1) };
  }
  const tz = slot.timezone || "UTC";
  const start = wallTimeToUtc(slot.date, slot.start as string, tz);
  let end: Date;
  if (slot.end && TIME_RE.test(slot.end)) {
    end = wallTimeToUtc(slot.date, slot.end, tz);
    // Créneau qui passe minuit (22:00–01:00) : la fin est le lendemain.
    if (end.getTime() <= start.getTime()) {
      end = wallTimeToUtc(addDays(slot.date, 1), slot.end, tz);
    }
  } else {
    end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60000);
  }
  return { allDay: false, kind: "time", start, end };
}

const utcStamp = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
const dayStamp = (day: string) => day.replace(/-/g, "");

function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/** Plie les lignes à 75 octets (RFC 5545 §3.1), sans couper un caractère UTF-8. */
function foldLine(line: string): string {
  const enc = new TextEncoder();
  if (enc.encode(line).length <= 75) return line;
  const out: string[] = [];
  let current = "";
  let bytes = 0;
  for (const ch of line) {
    const len = enc.encode(ch).length;
    const limit = out.length === 0 ? 75 : 74; // la suite commence par un espace
    if (bytes + len > limit) {
      out.push(current);
      current = "";
      bytes = 0;
    }
    current += ch;
    bytes += len;
  }
  out.push(current);
  return out.join("\r\n ");
}

/**
 * Fichier iCalendar d'une date fixée.
 * `method` : PUBLISH pour le téléchargement, REQUEST n'est pas utilisé (pas
 * d'organisateur à qui répondre — on ne veut pas de boutons Accepter/Refuser).
 */
export function buildFinalDateIcs(event: FinalDateEvent, now: Date = new Date()): string {
  const bounds = resolveEventBounds(event.slot);
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//DooDates//Final date//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];
  if (event.slot.timezone) lines.push(`X-WR-TIMEZONE:${event.slot.timezone}`);
  lines.push(
    "BEGIN:VEVENT",
    `UID:${event.uid}@doodates.com`,
    `SEQUENCE:${event.sequence ?? 0}`,
    `DTSTAMP:${utcStamp(now)}`,
  );
  if (bounds.kind === "day") {
    lines.push(`DTSTART;VALUE=DATE:${dayStamp(bounds.startDay)}`);
    lines.push(`DTEND;VALUE=DATE:${dayStamp(bounds.endDayExclusive)}`);
  } else {
    lines.push(`DTSTART:${utcStamp(bounds.start)}`);
    lines.push(`DTEND:${utcStamp(bounds.end)}`);
  }
  lines.push(`SUMMARY:${escapeIcsText(event.title)}`);
  if (event.description) lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  if (event.url) lines.push(`URL:${event.url}`);
  lines.push("STATUS:CONFIRMED", "TRANSP:OPAQUE", "END:VEVENT", "END:VCALENDAR");
  return lines.map(foldLine).join("\r\n") + "\r\n";
}

function descriptionWithUrl(event: FinalDateEvent): string {
  return [event.description, event.url].filter(Boolean).join("\n\n");
}

/** Lien de composition pour une cible web (tout sauf Apple, qui prend le .ics). */
export function buildCalendarUrl(
  target: Exclude<CalendarTarget, "apple">,
  event: FinalDateEvent,
): string {
  const bounds = resolveEventBounds(event.slot);
  const details = descriptionWithUrl(event);

  if (target === "google") {
    const p = new URLSearchParams({ action: "TEMPLATE", text: event.title });
    p.set(
      "dates",
      bounds.kind === "day"
        ? `${dayStamp(bounds.startDay)}/${dayStamp(bounds.endDayExclusive)}`
        : `${utcStamp(bounds.start)}/${utcStamp(bounds.end)}`,
    );
    if (details) p.set("details", details);
    return `https://calendar.google.com/calendar/render?${p.toString()}`;
  }

  if (target === "office365" || target === "outlook") {
    const host = target === "office365" ? "outlook.office.com" : "outlook.live.com";
    const p = new URLSearchParams({
      path: "/calendar/action/compose",
      rru: "addevent",
      subject: event.title,
    });
    if (bounds.kind === "day") {
      p.set("startdt", bounds.startDay);
      p.set("enddt", bounds.endDayExclusive);
      p.set("allday", "true");
    } else {
      p.set("startdt", bounds.start.toISOString());
      p.set("enddt", bounds.end.toISOString());
    }
    if (details) p.set("body", details);
    return `https://${host}/calendar/0/deeplink/compose?${p.toString()}`;
  }

  // Yahoo
  const p = new URLSearchParams({ v: "60", title: event.title });
  if (bounds.kind === "day") {
    p.set("st", dayStamp(bounds.startDay));
    p.set("dur", "allday");
  } else {
    p.set("st", utcStamp(bounds.start));
    p.set("et", utcStamp(bounds.end));
  }
  if (details) p.set("desc", details);
  return `https://calendar.yahoo.com/?${p.toString()}`;
}

/** Nom de fichier .ics sûr pour tous les systèmes. */
export function icsFileName(event: FinalDateEvent): string {
  const base = event.title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .toLowerCase();
  return `${base || "event"}-${event.slot.date}.ics`;
}

/** Déclenche le téléchargement du .ics dans le navigateur. */
export function downloadFinalDateIcs(event: FinalDateEvent): void {
  const blob = new Blob([buildFinalDateIcs(event)], {
    type: "text/calendar;charset=utf-8",
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = icsFileName(event);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(href), 0);
}
